import { useFieldArray, UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DayOfWeek } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { PlusIcon, XIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

type Props = {
  form: UseFormReturn<{
    weekDays: {
      dayOfWeek: DayOfWeek;
      startTime: string;
      endTime: string;
    }[];
  }>;
  title: string;
  description: string;
};

export const CalendarWeekDaysSubForm: React.FC<Props> = ({ form, title, description }) => {
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "weekDays",
  });

  return (
    <div className="border border-dashed border-gray-300 rounded-md xl:p-4 p-2">
      <div className="flex justify-between items-center">
        <FormLabel>{title}</FormLabel>
        <Button
          type="button"
          variant="outline"
          onClick={() => append({ dayOfWeek: DayOfWeek.Monday, startTime: "", endTime: "" })}
          size="sm"
        >
          <PlusIcon className="w-2 h-2" />
          {t("channel.addDay")}
        </Button>
      </div>
      <Alert variant="info" className="mt-2">
        <AlertDescription>{description}</AlertDescription>
      </Alert>
      {fields.map((field, index) => (
        <div key={field.id} className="flex gap-2 items-center mt-4">
          <FormField
            control={form.control}
            name={`weekDays.${index}.dayOfWeek`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("channel.dayOfWeek")}</FormLabel>
                <Select onValueChange={(value) => field.onChange(Number(value))} value={field.value?.toString()}>
                  <FormControl>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue placeholder={t("common.select")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(DayOfWeek)
                      .filter((item) => {
                        return Number(item) >= 0;
                      })
                      .map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {t(`channel.days.${day}`)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`weekDays.${index}.startTime`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="truncate">{t("channel.startTime")}</FormLabel>
                <FormControl>
                  <input
                    type="time"
                    {...field}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={`weekDays.${index}.endTime`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="truncate">{t("channel.endTime")}</FormLabel>
                <FormControl>
                  <input
                    type="time"
                    {...field}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <XIcon className="w-4 h-4 text-red-500 mt-2" onClick={() => remove(index)} />
        </div>
      ))}
    </div>
  );
};
